import { useState, type FormEvent, useEffect } from 'react';
import { DEFAULT_DASHBOARD_NODE_KEY } from '../../dashboard/constants';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowBack } from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { clearPasswordResetCooldowns, useRestorePassword } from '../hooks/useRestorePassword';
import { useAppSelector } from '../../../app/store';
import { useThemeMode } from '../../../theme/ThemeModeContext';
import { LOGIN_LOGO_DARK_URL, LOGIN_LOGO_LIGHT_URL } from '../constants';

const isInternalPath = (value: string | null | undefined): value is string =>
  Boolean(value?.startsWith('/') && !value.startsWith('//'));

export const LoginPage = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const { login, isAuthenticated, isLoading, error, resetError } = useAuth();
  const homePageUrl = useAppSelector((state) => state.auth.homePageUrl);
  const { mode: themeMode } = useThemeMode();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [mode, setMode] = useState<'login' | 'restore'>('login');

  const {
    email,
    setEmail,
    isEmailValid,
    isLoading: isResettingPassword,
    error: resetError_,
    successMessage,
    remainingSeconds,
    formatRemainingTime,
    handleSubmit: handleRestorePasswordSubmit,
  } = useRestorePassword();

  useEffect(() => {
    if (isAuthenticated) {
      const redirectPath =
        isInternalPath(search.redirect) && search.redirect !== '/'
          ? search.redirect
          : isInternalPath(homePageUrl)
            ? homePageUrl
            : '/';
      navigate({
        to: redirectPath,
        search: redirectPath === '/' ? { nodeKey: DEFAULT_DASHBOARD_NODE_KEY } : undefined,
      });
    }
  }, [homePageUrl, isAuthenticated, navigate, search.redirect]);

  const handleLoginSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!username || !password) {
      return;
    }

    try {
      await login({ username, password });

      clearPasswordResetCooldowns();
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const getErrorMessage = (error: any) => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (typeof error.message === 'string' && error.message.trim()) return error.message;
    if ('data' in error) {
      if (typeof error.data === 'string') return error.data;
      if (error.data && typeof error.data.message === 'string' && error.data.message.trim()) {
        return error.data.message;
      }
    }
    return 'Something went wrong';
  };

  useEffect(() => {
    if (!error) return;

    const timeout = setTimeout(() => {
      resetError();
    }, 300);

    return () => clearTimeout(timeout);
  }, [username, password, error, resetError]);

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper elevation={3} sx={{ padding: 4, width: '100%', maxWidth: 400 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <img
            src={themeMode === 'dark' ? LOGIN_LOGO_DARK_URL : LOGIN_LOGO_LIGHT_URL}
            alt="Logo"
            style={{ height: 66, width: 100 }}
          />
        </Box>

        {mode === 'login' ? (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {getErrorMessage(error)}
              </Alert>
            )}

            <Box component="form" onSubmit={handleLoginSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || !username || !password}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setMode('restore');
                  setEmail('');
                }}
                sx={{ color: 'text.secondary' }}
              >
                Restore password
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  setMode('login');
                  setEmail('');
                }}
                variant="text"
                size="small"
                disableRipple
                sx={{
                  justifyContent: 'flex-start',
                  p: 0,
                  minWidth: 0,
                  backgroundColor: 'transparent',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Back to Sign In
              </Button>
            </Box>

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
              Restore Password
            </Typography>

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {successMessage}
              </Alert>
            )}

            {resetError_ && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {getErrorMessage(resetError_)}
              </Alert>
            )}

            <Box
              onSubmit={async (e: FormEvent<HTMLFormElement>) => {
                e.preventDefault();
                await handleRestorePasswordSubmit();
              }}
              component="form"
              noValidate
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="restore-email"
                label="Email Address"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isResettingPassword || remainingSeconds > 0}
                helperText={
                  remainingSeconds > 0
                    ? `Please wait: ${formatRemainingTime()}`
                    : 'Enter your registered email address'
                }
                FormHelperTextProps={{
                  sx: { mx: 0 },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={!isEmailValid || isResettingPassword || remainingSeconds > 0}
              >
                {isResettingPassword ? (
                  <CircularProgress size={24} />
                ) : remainingSeconds > 0 ? (
                  `Please wait: ${formatRemainingTime()}`
                ) : (
                  'Restore password'
                )}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};
